import React, { useState, useEffect } from 'react';
import { AdminDashboard } from './AdminDashboard';
import { EnquiryCalendarView } from './EnquiryCalendarView';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Switch } from '../components/ui/switch';
import { getEnquiries, updateEnquiry, deleteEnquiry, convertEnquiryToBooking } from '../utils/api';
import { toast } from 'sonner';
import { Phone, Calendar as CalendarIcon, Trash2, Edit, Search, Bell, List, CalendarDays } from 'lucide-react';
import { format } from 'date-fns';

export const AdminEnquiries = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [filteredEnquiries, setFilteredEnquiries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isConvertDialogOpen, setIsConvertDialogOpen] = useState(false);
  const [editData, setEditData] = useState({
    status: '',
    notes: '',
    followUpDate: null,
    followUpReminder: false
  });
  const [bookingData, setBookingData] = useState({
    amount: '',
    advancePaid: '',
    balanceDue: '',
    eventTimeFrom: '07:00 AM',
    eventTimeTo: '08:00 PM',
    notes: ''
  });

  useEffect(() => {
    loadEnquiries();
  }, []);

  useEffect(() => {
    filterEnquiries();
  }, [searchTerm, statusFilter, enquiries]);

  const loadEnquiries = async () => {
    setIsLoading(true);
    try {
      const response = await getEnquiries();
      setEnquiries(response.enquiries);
    } catch (error) {
      toast.error('Failed to load enquiries');
    } finally {
      setIsLoading(false);
    }
  };

  const filterEnquiries = () => {
    let filtered = [...enquiries];

    if (statusFilter !== 'all') {
      filtered = filtered.filter(e => e.status === statusFilter);
    }

    if (searchTerm) {
      filtered = filtered.filter(e => 
        e.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.phone.includes(searchTerm) ||
        e.eventType.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredEnquiries(filtered);
  };

  const handleEdit = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setEditData({
      status: enquiry.status,
      notes: enquiry.notes || '',
      followUpDate: enquiry.followUpDate || null,
      followUpReminder: enquiry.followUpReminder || false
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    try {
      const updatePayload = {
        status: editData.status,
        notes: editData.notes,
        followUpReminder: editData.followUpReminder
      };
      
      if (editData.followUpDate) {
        updatePayload.followUpDate = editData.followUpDate; // Already in YYYY-MM-DD format from date input
      }

      await updateEnquiry(selectedEnquiry.id, updatePayload);
      toast.success('Enquiry updated successfully');
      setIsEditDialogOpen(false);
      loadEnquiries();
    } catch (error) {
      toast.error('Failed to update enquiry');
    }
  };

  const handleDelete = async (enquiryId) => {
    if (!window.confirm('Are you sure you want to delete this enquiry?')) return;

    try {
      await deleteEnquiry(enquiryId);
      toast.success('Enquiry deleted successfully');
      loadEnquiries();
    } catch (error) {
      toast.error('Failed to delete enquiry');
    }
  };

  const handleCall = (phone) => {
    window.location.href = `tel:${phone}`;
  };

  const handleConvertToBooking = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setBookingData({
      eventDate: enquiry.eventDate || null,
      eventEndDate: enquiry.eventEndDate || null,
      amount: '',
      advancePaid: '',
      balanceDue: '',
      eventTimeFrom: enquiry.eventTimeFrom || '09:00 AM',
      eventTimeTo: enquiry.eventTimeTo || '10:00 PM',
      notes: ''
    });
    setIsConvertDialogOpen(true);
  };

  const handleConvert = async () => {
    if (!bookingData.amount || !bookingData.advancePaid) {
      toast.error('Please fill amount and advance paid');
      return;
    }

    if (!bookingData.eventDate) {
      toast.error('Please select event start date');
      return;
    }

    try {
      const balanceDue = parseFloat(bookingData.amount) - parseFloat(bookingData.advancePaid);
      const convertData = {
        eventDate: bookingData.eventDate, // Already in YYYY-MM-DD format from date input
        eventEndDate: bookingData.eventEndDate || null,
        eventTimeFrom: bookingData.eventTimeFrom,
        eventTimeTo: bookingData.eventTimeTo,
        amount: parseFloat(bookingData.amount),
        advancePaid: parseFloat(bookingData.advancePaid),
        balanceDue: balanceDue,
        notes: bookingData.notes
      };

      await convertEnquiryToBooking(selectedEnquiry.id, convertData);
      toast.success('Enquiry converted to booking successfully!');
      setIsConvertDialogOpen(false);
      loadEnquiries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert enquiry');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'contacted': return 'bg-yellow-100 text-yellow-800';
      case 'follow-up': return 'bg-orange-100 text-orange-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <AdminDashboard currentPage="enquiries">
      <div className="space-y-4 sm:space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Enquiries Management</h2>
          <div className="flex flex-wrap gap-2">
            {/* View Toggle */}
            <div className="flex border rounded-lg">
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className={`text-xs sm:text-sm ${viewMode === 'list' ? 'bg-[#D4AF37] text-black hover:bg-[#C19B2E]' : ''}`}
              >
                <List className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">List</span>
              </Button>
              <Button
                variant={viewMode === 'calendar' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('calendar')}
                className={`text-xs sm:text-sm ${viewMode === 'calendar' ? 'bg-[#D4AF37] text-black hover:bg-[#C19B2E]' : ''}`}
              >
                <CalendarDays className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Calendar</span>
              </Button>
            </div>
            <Button onClick={loadEnquiries} variant="outline" size="sm" className="text-xs sm:text-sm">
              Refresh
            </Button>
          </div>
        </div>

        {/* Filters - Only show in list view */}
        {viewMode === 'list' && (
        <div className="bg-white p-3 sm:p-4 rounded-lg shadow space-y-3 sm:space-y-0 sm:flex sm:items-center sm:gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 text-sm"
              />
            </div>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-48 text-sm">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="follow-up">Follow-up</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        )}

        {/* Calendar View */}
        {viewMode === 'calendar' && (
          <EnquiryCalendarView 
            enquiries={enquiries} 
            onDateClick={(enquiry) => handleEdit(enquiry)}
          />
        )}

        {/* List View - Mobile Cards / Desktop Table */}
        {viewMode === 'list' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center">Loading enquiries...</div>
          ) : filteredEnquiries.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No enquiries found</div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="block sm:hidden divide-y divide-gray-200">
                {filteredEnquiries.map((enquiry) => (
                  <div key={enquiry.id} className="p-4 space-y-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{enquiry.name}</p>
                        <button
                          onClick={() => handleCall(enquiry.phone)}
                          className="flex items-center text-blue-600 text-sm"
                        >
                          <Phone className="w-3 h-3 mr-1" />
                          {enquiry.phone}
                        </button>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(enquiry.status)}`}>
                        {enquiry.status}
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <CalendarIcon className="w-3 h-3 mr-1" />
                      {format(new Date(enquiry.eventDate), 'MMM dd, yyyy')} • {enquiry.eventType}
                    </div>
                    {enquiry.followUpReminder && enquiry.followUpDate && (
                      <div className="flex items-center text-sm text-orange-600">
                        <Bell className="w-3 h-3 mr-1" />
                        Follow-up: {format(new Date(enquiry.followUpDate), 'MMM dd')}
                      </div>
                    )}
                    <div className="flex gap-2 pt-2">
                      {enquiry.status !== 'closed' && (
                        <Button
                          onClick={() => handleConvertToBooking(enquiry)}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white text-xs flex-1"
                        >
                          Convert
                        </Button>
                      )}
                      <Button
                        onClick={() => handleEdit(enquiry)}
                        variant="outline"
                        size="sm"
                        className="flex-1 text-xs"
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        onClick={() => handleDelete(enquiry.id)}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 text-xs"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event Date</th>
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event Type</th>
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Follow-up</th>
                      <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredEnquiries.map((enquiry) => (
                      <tr key={enquiry.id} className="hover:bg-gray-50">
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                          <div className="font-medium text-gray-900 text-sm">{enquiry.name}</div>
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleCall(enquiry.phone)}
                            className="flex items-center text-blue-600 hover:text-blue-800 text-sm"
                          >
                            <Phone className="w-4 h-4 mr-1" />
                            {enquiry.phone}
                          </button>
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-gray-900 text-sm">
                            <CalendarIcon className="w-4 h-4 mr-2 text-gray-400" />
                            {format(new Date(enquiry.eventDate), 'MMM dd, yyyy')}
                          </div>
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-gray-900 text-sm">{enquiry.eventType}</td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(enquiry.status)}`}>
                            {enquiry.status}
                          </span>
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                          {enquiry.followUpReminder && enquiry.followUpDate ? (
                            <div className="flex items-center text-orange-600">
                              <Bell className="w-4 h-4 mr-1" />
                              <span className="text-xs">{format(new Date(enquiry.followUpDate), 'MMM dd')}</span>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">None</span>
                          )}
                        </td>
                        <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex gap-2">
                            {enquiry.status !== 'closed' && (
                              <Button
                                onClick={() => handleConvertToBooking(enquiry)}
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white"
                              >
                                Convert
                              </Button>
                            )}
                            <Button
                              onClick={() => handleEdit(enquiry)}
                              variant="outline"
                              size="sm"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              onClick={() => handleDelete(enquiry.id)}
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Enquiry</DialogTitle>
            <DialogDescription>
              Update enquiry status and set follow-up reminders
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={editData.status} onValueChange={(value) => setEditData({ ...editData, status: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="follow-up">Follow-up</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Follow-up Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {editData.followUpDate ? format(editData.followUpDate, 'PPP') : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={editData.followUpDate}
                    onSelect={(date) => setEditData({ ...editData, followUpDate: date })}
                    disabled={(date) => date < new Date()}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                checked={editData.followUpReminder}
                onCheckedChange={(checked) => setEditData({ ...editData, followUpReminder: checked })}
              />
              <Label>Enable Follow-up Reminder</Label>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={editData.notes}
                onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                placeholder="Add any notes..."
                rows={4}
              />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleUpdate} className="flex-1 bg-[#D4AF37] text-black hover:bg-[#C19B2E]">
                Update
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Convert to Booking Dialog */}
      <Dialog open={isConvertDialogOpen} onOpenChange={setIsConvertDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Convert Enquiry to Booking</DialogTitle>
            <DialogDescription>
              Enter booking details to confirm this enquiry
            </DialogDescription>
          </DialogHeader>
          {selectedEnquiry && (
            <div className="space-y-4 mt-4 pb-4">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm font-semibold text-blue-900 mb-1">Original Enquiry Details:</p>
                <p className="text-sm"><b>Customer:</b> {selectedEnquiry.name}</p>
                <p className="text-sm"><b>Phone:</b> {selectedEnquiry.phone}</p>
                <p className="text-sm"><b>Event:</b> {selectedEnquiry.eventType}</p>
                <p className="text-sm"><b>Requested Date:</b> {(() => {
                  const dateStr = selectedEnquiry.eventDate;
                  const [year, month, day] = dateStr.split('-').map(Number);
                  const localDate = new Date(year, month - 1, day);
                  return format(localDate, 'MMM dd, yyyy');
                })()}</p>
                {selectedEnquiry.eventEndDate && (
                  <p className="text-sm"><b>End Date:</b> {(() => {
                    const dateStr = selectedEnquiry.eventEndDate;
                    const [year, month, day] = dateStr.split('-').map(Number);
                    const localDate = new Date(year, month - 1, day);
                    return format(localDate, 'MMM dd, yyyy');
                  })()}</p>
                )}
              </div>

              <div className="p-3 bg-yellow-50 border border-yellow-300 rounded">
                <p className="text-sm text-yellow-800">
                  <b>💡 Tip:</b> You can adjust dates and times based on customer needs (e.g., add preparation days)
                </p>
              </div>

              {/* Date Selection */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Booking Dates *</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Event Start Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {bookingData.eventDate ? format(bookingData.eventDate, 'PPP') : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          key={`start-${bookingData.eventDate ? bookingData.eventDate.toISOString() : 'none'}`}
                          mode="single"
                          selected={bookingData.eventDate}
                          onSelect={(date) => setBookingData({ ...bookingData, eventDate: date })}
                          defaultMonth={bookingData.eventDate || new Date()}
                          disabled={(date) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            return date < today;
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>Event End Date (Optional)</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {bookingData.eventEndDate ? format(bookingData.eventEndDate, 'PPP') : 'Same day'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          key={`end-${bookingData.eventDate ? bookingData.eventDate.toISOString() : 'none'}`}
                          mode="single"
                          selected={bookingData.eventEndDate}
                          onSelect={(date) => setBookingData({ ...bookingData, eventEndDate: date })}
                          defaultMonth={bookingData.eventDate || new Date()}
                          disabled={(date) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            return (bookingData.eventDate && date < bookingData.eventDate) || date < today;
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                {bookingData.eventEndDate && (
                  <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                    <p className="text-sm text-green-800">
                      <b>Multi-day:</b> {format(bookingData.eventDate, 'MMM dd')} - {format(bookingData.eventEndDate, 'MMM dd, yyyy')}
                    </p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setBookingData({ ...bookingData, eventEndDate: null })}
                      className="text-xs"
                    >
                      Clear
                    </Button>
                  </div>
                )}
              </div>

              {/* Time Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Event Time From</Label>
                  <Input
                    type="time"
                    value={bookingData.eventTimeFrom}
                    onChange={(e) => setBookingData({ ...bookingData, eventTimeFrom: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Event Time To</Label>
                  <Input
                    type="time"
                    value={bookingData.eventTimeTo}
                    onChange={(e) => setBookingData({ ...bookingData, eventTimeTo: e.target.value })}
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">Business hours: 9:00 AM - 10:00 PM</p>

              <div className="space-y-2">
                <Label>Total Amount (Rs.) *</Label>
                <Input
                  type="number"
                  placeholder="Enter total amount"
                  value={bookingData.amount}
                  onChange={(e) => {
                    const amount = e.target.value;
                    const advance = bookingData.advancePaid || 0;
                    setBookingData({
                      ...bookingData,
                      amount: amount,
                      balanceDue: (parseFloat(amount) - parseFloat(advance)).toString()
                    });
                  }}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Advance Paid (Rs.) *</Label>
                <Input
                  type="number"
                  placeholder="Enter advance paid"
                  value={bookingData.advancePaid}
                  onChange={(e) => {
                    const advance = e.target.value;
                    const amount = bookingData.amount || 0;
                    setBookingData({
                      ...bookingData,
                      advancePaid: advance,
                      balanceDue: (parseFloat(amount) - parseFloat(advance)).toString()
                    });
                  }}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Balance Due (Rs.)</Label>
                <Input
                  type="number"
                  value={bookingData.balanceDue}
                  disabled
                  className="bg-gray-100"
                />
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={bookingData.notes}
                  onChange={(e) => setBookingData({ ...bookingData, notes: e.target.value })}
                  placeholder="Add booking notes..."
                  rows={3}
                />
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setIsConvertDialogOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleConvert} className="flex-1 bg-green-600 hover:bg-green-700 text-white">
                  Confirm Booking
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminDashboard>
  );
};